/**
 * Unified deploy command: `mastra deploy [--env <name>]`
 *
 * This is the new entry point for deploying Mastra projects.
 * It replaces `mastra studio deploy` and `mastra server deploy`.
 *
 * - Auto-creates project if missing (from package.json name)
 * - Auto-creates environment if missing (with prompt or --yes)
 * - Deploys to the specified environment (default: production)
 */

import { execSync } from 'node:child_process';
import { createWriteStream } from 'node:fs';
import { mkdir, rm, stat, access, readFile, writeFile } from 'node:fs/promises';
import { tmpdir } from 'node:os';
import { join, resolve } from 'node:path';
import * as p from '@clack/prompts';
import { coreFeatures } from '@mastra/core/features';
import { ZipArchive } from 'archiver';
import pc from 'picocolors';

import { bucketApiHost, getAnalytics } from '../../analytics/index.js';
import type { CLI_ORIGIN } from '../../analytics/index.js';
import { createBarLogWriter } from '../../utils/clack-bar.js';
import { deployDashboardUrl, printDeployFailure } from '../../utils/deploy-failure-output.js';
import { createLogCollector } from '../../utils/deploy-log-format.js';
import type { DeployLogWriter, LogCollector } from '../../utils/deploy-log-format.js';
import { detectProjectType } from '../../utils/detect-project-type.js';
import { abortableDelay } from '../../utils/polling.js';
import { runBuild } from '../../utils/run-build.js';
import { checkBuildStaleness } from '../../utils/source-hash.js';
import { fetchOrgs } from '../auth/api.js';
import { MASTRA_PLATFORM_API_URL, MASTRA_STUDIO_URL } from '../auth/client.js';
import { getToken, getCurrentOrgId, loadCredentials } from '../auth/credentials.js';
import { fetchDatabases } from '../db/platform-api.js';
import type { ProjectDatabase } from '../db/platform-api.js';
import {
  mergePreflightEnvVars,
  preflightBuildOutput,
  printPreflightIssues,
  hasWorkersRedisRequirement,
} from '../deploy-preflight.js';
import { fetchEnvironments, fetchProjects, createEnvironment } from '../env/platform-api.js';
import type { Environment } from '../env/platform-api.js';
import { createServerProject } from '../server/platform-api.js';
import type { ServerProjectRegion } from '../server/platform-api.js';
import { getDeployEnvFiles, loadDeployEnvFromDotenv, readEnvVars, getMastraVersion } from '../studio/deploy.js';
import { createProject, fetchProjects as fetchStudioProjects } from '../studio/platform-api.js';
import { getProjectConfigToSave, loadProjectConfig, saveProjectConfig } from '../studio/project-config.js';
import { maybeAutoProvisionDatabases } from './auto-provision-database.js';
import { getOverwrittenEnvKeys } from './env-vars.js';
import { assertDeployDir } from './validate-dir.js';

/**
 * Derive the public studio/server URLs from the environment slug.
 * These are the user-facing URLs, not the internal Railway instanceUrl.
 */
function derivePublicUrls(
  slug: string,
  projectType?: string,
): { studioUrl: string; serverUrl: string; serverLabel: string } {
  // Determine if we're targeting staging or production
  const isStaging = MASTRA_PLATFORM_API_URL.includes('staging');
  const baseDomain = isStaging ? 'staging.mastra.cloud' : 'mastra.cloud';
  const isFactory = projectType === 'factory';
  const serverSubdomain = isFactory ? 'factory' : 'server';

  return {
    studioUrl: `https://${slug}.studio.${baseDomain}`,
    serverUrl: `https://${slug}.${serverSubdomain}.${baseDomain}`,
    serverLabel: isFactory ? 'Factory' : 'Server',
  };
}

function elapsed(ms: number): string {
  return ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(1)}s`;
}

const workersManifestPath = (targetDir: string): string => join(targetDir, '.mastra', 'output', 'workers.json');
const workerManifestCheckPath = (targetDir: string): string => join(targetDir, '.mastra', 'worker-manifest-checked');
const WORKER_MANIFEST_CHECK_VERSION = '2';

async function hasWorkersManifest(targetDir: string): Promise<boolean> {
  try {
    await access(workersManifestPath(targetDir));
    return true;
  } catch {
    return false;
  }
}

export async function hasWorkerManifestCheck(targetDir: string): Promise<boolean> {
  try {
    const version = await readFile(workerManifestCheckPath(targetDir), 'utf-8');
    return version.trim() === WORKER_MANIFEST_CHECK_VERSION;
  } catch {
    return false;
  }
}

export function deployBuildNeedsRefresh(
  staleness: { isStale: boolean },
  workersManifestExists: boolean,
  workerManifestChecked: boolean,
): boolean {
  return staleness.isStale || (!workersManifestExists && !workerManifestChecked);
}

interface WorkerManifestSection {
  enabled: boolean;
  [key: string]: unknown;
}

interface WorkerManifestV1 extends Record<string, unknown> {
  version: 1;
  orchestration: WorkerManifestSection;
  scheduler: WorkerManifestSection;
  backgroundTasks: WorkerManifestSection;
  custom: string[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isWorkerManifestV1(value: Record<string, unknown> | null): value is WorkerManifestV1 {
  return (
    value?.version === 1 &&
    isRecord(value.orchestration) &&
    isRecord(value.scheduler) &&
    isRecord(value.backgroundTasks) &&
    Array.isArray(value.custom) &&
    value.custom.every(name => typeof name === 'string')
  );
}

async function readWorkersConfig(targetDir: string): Promise<Record<string, unknown> | null> {
  try {
    const raw = await readFile(workersManifestPath(targetDir), 'utf-8');
    const manifest = JSON.parse(raw) as unknown;
    return isRecord(manifest) ? manifest : null;
  } catch {
    return null;
  }
}

function workerManifestHasEnabledWorkers(manifest: Record<string, unknown> | null): boolean {
  if (isWorkerManifestV1(manifest)) {
    return (
      manifest.orchestration.enabled === true ||
      manifest.scheduler.enabled === true ||
      manifest.backgroundTasks.enabled === true ||
      manifest.custom.length > 0
    );
  }
  return manifest?.enabled === true;
}

export async function hasEnabledWorkers(targetDir: string): Promise<boolean> {
  return workerManifestHasEnabledWorkers(await readWorkersConfig(targetDir));
}

export type WorkersDeployMode = 'dedicated' | 'in-process';

/**
 * `--workers dedicated` was requested but the deploy env can't satisfy the
 * platform's Redis (pub/sub) coordination requirement. Hard error: an
 * explicit flag must not silently degrade to in-process.
 */
export class WorkersRedisRequirementError extends Error {}

/**
 * Decide whether this deploy should provision a dedicated workers service
 * ("dedicated") or run background tasks in-process inside the API server
 * container ("in-process").
 *
 * The workers manifest ships (→ dedicated) only when the build emitted
 * enabled workers AND the deploy env meets the Redis (pub/sub) requirement,
 * AND one of:
 *   - the environment already has a workers service,
 *   - the user passed `--workers dedicated`,
 *   - the deploy is non-interactive / `--yes`,
 *   - the user answers yes at the prompt.
 *
 * `--workers in-process` always wins. `--workers dedicated` without the
 * Redis requirement throws {@link WorkersRedisRequirementError} instead of
 * degrading; the implicit paths degrade to in-process (call site warns).
 */
export async function resolveWorkersDeployMode(input: {
  workersEnabled: boolean;
  redisRequirementMet: boolean;
  environmentHasWorkerService: boolean;
  workersOption: WorkersDeployMode | undefined;
  autoAccept: boolean;
  promptConfirm: (message: string) => Promise<boolean | symbol>;
  isCancel: (value: unknown) => value is symbol;
}): Promise<WorkersDeployMode> {
  if (input.workersOption === 'in-process') return 'in-process';
  // No enabled workers in the build → nothing to provision, mode is
  // irrelevant (an explicit `--workers dedicated` gets a warning at the
  // call site).
  if (!input.workersEnabled) return 'in-process';
  if (input.workersOption === 'dedicated') {
    if (!input.redisRequirementMet) {
      throw new WorkersRedisRequirementError(
        'A dedicated workers service requires Redis for coordination (pub/sub), but the deploy env has no usable REDIS_URL. Add REDIS_URL to your env file.',
      );
    }
    return 'dedicated';
  }
  if (!input.redisRequirementMet) return 'in-process';
  if (input.environmentHasWorkerService) return 'dedicated';
  if (input.autoAccept) return 'dedicated';

  const answer = await input.promptConfirm(
    'Provision a dedicated workers service? (recommended — otherwise background tasks run in-process inside the API server container)',
  );
  if (input.isCancel(answer)) return 'dedicated';
  return answer === false ? 'in-process' : 'dedicated';
}

/**
 * Rollout gate: evaluate the `platform-workers` PostHog flag as the
 * authenticated platform user, retaining the org as group context.
 *
 * Headless auth has no user id, so it falls back to organization targeting.
 * Fails closed — a PostHog error or disabled telemetry (`analytics` null)
 * suppresses workers for this deploy without mutating the reusable build output.
 */
export async function applyPlatformWorkersFlagGate(deps: {
  orgId: string;
  userId?: string;
  analytics: {
    isFeatureEnabled(
      flag: string,
      options?: { distinctId?: string; groups?: Record<string, string> },
    ): Promise<boolean>;
  } | null;
}): Promise<'preserved' | 'suppressed'> {
  const flagOn = deps.analytics
    ? await deps.analytics.isFeatureEnabled('platform-workers', {
        ...(deps.userId ? { distinctId: deps.userId } : {}),
        groups: { organization: deps.orgId },
      })
    : false;
  return flagOn ? 'preserved' : 'suppressed';
}

type ArchitectureColors = ReturnType<typeof pc.createColors>;
type ArchitectureTone = 'blue' | 'cyan' | 'green' | 'gray' | 'magenta' | 'orange' | 'red' | 'yellow';

const UNITED_STATES_DEPLOY_LOCATION = 'United States';
const EUROPE_DEPLOY_LOCATION = 'Europe';

interface ArchitectureNode {
  title: string;
  subtitle: string;
  tone: ArchitectureTone;
}

const BOX_INNER_WIDTH = 30;
const BOX_WIDTH = BOX_INNER_WIDTH + 2;
const BOX_TEXT_WIDTH = BOX_INNER_WIDTH - 2;
const BOX_HEIGHT = 4;
const SLOT_HEIGHT = BOX_HEIGHT + 1;
const CONNECTOR_GAP_WIDTH = 7;
const CONNECTOR_SPINE_X = Math.floor(CONNECTOR_GAP_WIDTH / 2);

const DATABASE_PRESENTATION: Record<ProjectDatabase['kind'], { label: string; tone: ArchitectureTone }> = {
  turso: { label: 'Turso', tone: 'cyan' },
  neon: { label: 'Neon', tone: 'green' },
  mongodb: { label: 'MongoDB', tone: 'green' },
  redis: { label: 'Redis', tone: 'red' },
};

function architectureTextWidth(value: string): number {
  return Array.from(value).length;
}

function truncateArchitectureText(value: string, width = BOX_TEXT_WIDTH): string {
  const characters = Array.from(value);
  if (characters.length <= width) return value;
  return `${characters.slice(0, width - 1).join('')}…`;
}

const DEPLOY_REGION_PRESENTATION: Array<{
  matches: (region: string) => boolean;
  label: string;
  location: string;
}> = [
  {
    matches: region => region === 'eu' || region === 'ams' || region.startsWith('europe-'),
    label: 'EU West',
    location: EUROPE_DEPLOY_LOCATION,
  },
  {
    matches: region => region === 'iad' || region.startsWith('us-east'),
    label: 'US East',
    location: UNITED_STATES_DEPLOY_LOCATION,
  },
  {
    matches: region => region === 'sfo',
    label: 'US West (SF)',
    location: UNITED_STATES_DEPLOY_LOCATION,
  },
  {
    matches: region => region === 'us' || region === 'pdx' || region.startsWith('us-west'),
    label: 'US West',
    location: UNITED_STATES_DEPLOY_LOCATION,
  },
];

function getDeploymentRegionPresentation(region: string | null): { label: string; location: string } {
  const normalized = region?.trim().toLowerCase();
  if (!normalized) return { label: 'US West', location: UNITED_STATES_DEPLOY_LOCATION };
  return (
    DEPLOY_REGION_PRESENTATION.find(presentation => presentation.matches(normalized)) ?? {
      label: region ?? 'US West',
      location: UNITED_STATES_DEPLOY_LOCATION,
    }
  );
}

function formatDeploymentLocation(region: string | null): string {
  return getDeploymentRegionPresentation(region).location;
}

function formatDeploymentRegion(region: string | null): string {
  return getDeploymentRegionPresentation(region).label;
}

function formatArchitectureDate(date: Date): string {
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

function paintArchitectureTone(colors: ArchitectureColors, tone: ArchitectureTone, value: string): string {
  switch (tone) {
    case 'blue':
      return colors.blue(value);
    case 'cyan':
      return colors.cyan(value);
    case 'green':
      return colors.green(value);
    case 'gray':
      return colors.gray(value);
    case 'magenta':
      return colors.magenta(value);
    case 'orange':
      return colors.isColorSupported ? `\u001B[38;5;214m${value}\u001B[39m` : value;
    case 'red':
      return colors.red(value);
    case 'yellow':
      return colors.yellow(value);
  }
}

function formatWorkersConfigName(name: string): string {
  return name
    .replace(/Ms$/, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\bTtl\b/g, 'TTL')
    .replace(/\bUrl\b/g, 'URL')
    .replace(/\bId\b/g, 'ID')
    .replace(/\b\w/g, character => character.toUpperCase());
}

function formatDuration(ms: number): string {
  const units = [
    ['day', 86_400_000],
    ['hour', 3_600_000],
    ['minute', 60_000],
    ['second', 1_000],
  ] as const;
  for (const [unit, unitMs] of units) {
    if (ms >= unitMs && ms % unitMs === 0) {
      const amount = ms / unitMs;
      return `${amount} ${unit}${amount === 1 ? '' : 's'}`;
    }
  }
  return `${ms} ms`;
}

function formatWorkersConfigValue(name: string, value: unknown): string {
  if (typeof value === 'number' && name.endsWith('Ms')) return formatDuration(value);
  if (typeof value === 'string') return value.replace(/(^|[-_ ])\w/g, match => match.toUpperCase());
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.map(item => String(item)).join(', ');
  return String(value);
}

function flattenWorkersConfig(
  config: Record<string, unknown>,
  prefix: string[] = [],
): Array<{ name: string; value: string }> {
  const entries: Array<{ name: string; value: string }> = [];
  for (const [name, value] of Object.entries(config)) {
    if (name === 'enabled') continue;
    if (isRecord(value)) {
      entries.push(...flattenWorkersConfig(value, [...prefix, formatWorkersConfigName(name)]));
      continue;
    }
    entries.push({
      name: [...prefix, formatWorkersConfigName(name)].join(' · '),
      value: formatWorkersConfigValue(name, value),
    });
  }
  return entries;
}

function renderWorkerConfigItem(
  title: string,
  enabled: boolean,
  details: Array<{ name?: string; value: string }>,
  colors: ArchitectureColors,
): string[] {
  const dot = enabled ? colors.green('●') : colors.gray('●');
  const label = enabled ? colors.bold(colors.white(title)) : colors.gray(title);
  const detailColor = enabled ? colors.yellow : colors.gray;
  return [
    `${dot} ${label}`,
    ...details.map(({ name, value }) =>
      name ? `    ${colors.dim(name)}: ${detailColor(value)}` : `    ${detailColor(value)}`,
    ),
  ];
}

function renderVersionedWorkersConfig(manifest: WorkerManifestV1, colors: ArchitectureColors): string[] {
  const customEnabled = manifest.custom.length > 0;
  return [
    ...renderWorkerConfigItem(
      'Orchestration',
      manifest.orchestration.enabled === true,
      flattenWorkersConfig(manifest.orchestration),
      colors,
    ),
    ...renderWorkerConfigItem(
      'Scheduler',
      manifest.scheduler.enabled === true,
      flattenWorkersConfig(manifest.scheduler),
      colors,
    ),
    ...renderWorkerConfigItem(
      'Background Tasks',
      manifest.backgroundTasks.enabled === true,
      flattenWorkersConfig(manifest.backgroundTasks),
      colors,
    ),
    ...renderWorkerConfigItem(
      'Custom',
      customEnabled,
      manifest.custom.map(workerName => ({ value: workerName })),
      colors,
    ),
  ];
}

function renderDeploymentPanel(
  input: {
    projectName: string;
    environment: Pick<Environment, 'name' | 'region'>;
    workersEnabled: boolean;
    workersConfig: Record<string, unknown> | null;
    showWorkersConfig: boolean;
    renderedAt: Date;
  },
  colors: ArchitectureColors,
): string[] {
  const workersConfigLines = isWorkerManifestV1(input.workersConfig)
    ? renderVersionedWorkersConfig(input.workersConfig, colors)
    : input.workersEnabled && input.workersConfig
      ? Object.entries(input.workersConfig)
          .filter(([name]) => name !== 'enabled')
          .map(
            ([name, value]) =>
              `• ${colors.bold(formatWorkersConfigName(name))}: ${colors.yellow(formatWorkersConfigValue(name, value))}`,
          )
      : [`• ${colors.bold('Status')}: ${colors.yellow(input.workersEnabled ? 'Enabled' : 'Disabled')}`];

  return [
    colors.bold(input.projectName),
    colors.bold(`${input.environment.name} (${formatDeploymentRegion(input.environment.region)})`),
    colors.dim(formatArchitectureDate(input.renderedAt)),
    ...(input.showWorkersConfig
      ? [
          '',
          colors.bold('Workers Config'),
          colors.dim('Static analysis only; runtime workers may differ.'),
          ...workersConfigLines,
        ]
      : []),
  ];
}

function visibleArchitectureWidth(value: string): number {
  return architectureTextWidth(value.replace(/\u001B\[[0-?]*[ -/]*[@-~]/g, ''));
}

function renderArchitectureBox(node: ArchitectureNode | undefined, colors: ArchitectureColors): string[] {
  if (!node) return Array.from({ length: BOX_HEIGHT }, () => ' '.repeat(BOX_WIDTH));

  const title = truncateArchitectureText(node.title);
  const subtitle = truncateArchitectureText(node.subtitle);
  const border = (value: string) => paintArchitectureTone(colors, node.tone, value);
  return [
    border(`┌${'─'.repeat(BOX_INNER_WIDTH)}┐`),
    `${border('│')} ${colors.bold(title)}${' '.repeat(BOX_TEXT_WIDTH - architectureTextWidth(title))} ${border('│')}`,
    `${border('│')} ${colors.dim(subtitle)}${' '.repeat(BOX_TEXT_WIDTH - architectureTextWidth(subtitle))} ${border('│')}`,
    border(`└${'─'.repeat(BOX_INNER_WIDTH)}┘`),
  ];
}

function connectorJunction(up: boolean, down: boolean, left: boolean, right: boolean): string {
  if (up && down && left && right) return '┼';
  if (up && down && left) return '┤';
  if (up && down && right) return '├';
  if (down && left && right) return '┬';
  if (up && left && right) return '┴';
  if (down && right) return '┌';
  if (down && left) return '┐';
  if (up && right) return '└';
  if (up && left) return '┘';
  if (left || right) return '─';
  return '│';
}

function renderConnectorGap(
  y: number,
  nodeConnectorYs: ReadonlySet<number>,
  centerConnectorY: number,
  side: 'left' | 'right',
  colors: ArchitectureColors,
): string {
  const connectorYs = [...nodeConnectorYs, centerConnectorY];
  const minY = Math.min(...connectorYs);
  const maxY = Math.max(...connectorYs);
  if (y < minY || y > maxY) return ' '.repeat(CONNECTOR_GAP_WIDTH);

  const hasNode = nodeConnectorYs.has(y);
  const left = side === 'left' ? hasNode : y === centerConnectorY;
  const right = side === 'left' ? y === centerConnectorY : hasNode;
  const cells = Array.from({ length: CONNECTOR_GAP_WIDTH }, () => ' ');

  if (left) {
    for (let x = 0; x < CONNECTOR_SPINE_X; x++) cells[x] = '─';
  }
  if (right) {
    for (let x = CONNECTOR_SPINE_X + 1; x < CONNECTOR_GAP_WIDTH; x++) cells[x] = '─';
  }
  cells[CONNECTOR_SPINE_X] = connectorJunction(y > minY, y < maxY, left, right);

  return colors.dim(cells.join(''));
}

export function renderDeploymentArchitecture(
  input: {
    projectName: string;
    environment: Pick<Environment, 'id' | 'name' | 'region'>;
    serverLabel: string;
    workersEnabled: boolean;
    workersConfig: Record<string, unknown> | null;
    showWorkersConfig?: boolean;
    databases: readonly ProjectDatabase[];
    observabilityEnabled: boolean;
    renderedAt?: Date;
  },
  colors: ArchitectureColors = pc,
): string {
  const databases = input.databases
    .filter(
      database =>
        database.deletedAt === null &&
        (database.environmentId === null || database.environmentId === input.environment.id) &&
        (database.status === 'ready' || database.status === 'provisioning'),
    )
    .sort((left, right) => left.name.localeCompare(right.name));

  const leftNodes: ArchitectureNode[] = [
    { title: 'Studio', subtitle: 'Project studio', tone: 'blue' },
    {
      title: input.serverLabel,
      subtitle: 'API service',
      tone: input.serverLabel === 'Factory' ? 'orange' : 'magenta',
    },
    ...(input.workersEnabled ? [{ title: 'Workers', subtitle: 'Worker runtime', tone: 'yellow' as const }] : []),
  ];
  const rightNodes: ArchitectureNode[] = [
    ...databases.map(database => {
      const presentation = DATABASE_PRESENTATION[database.kind];
      return {
        title: database.name,
        subtitle: `${presentation.label} · ${database.status === 'ready' ? 'Connected' : 'Provisioning'}`,
        tone: presentation.tone,
      };
    }),
    ...(input.observabilityEnabled
      ? [{ title: 'Observability', subtitle: 'Mastra Platform', tone: 'green' as const }]
      : []),
  ];

  const slotCount = Math.max(leftNodes.length, rightNodes.length);
  const centerSlot = Math.floor((slotCount - 1) / 2);
  const centerNode: ArchitectureNode = {
    title: input.environment.name,
    subtitle: formatDeploymentLocation(input.environment.region),
    tone: 'gray',
  };
  const connectorLineOffset = 2;
  const centerConnectorY = centerSlot * SLOT_HEIGHT + connectorLineOffset;
  const leftConnectorYs = new Set(leftNodes.map((_, index) => index * SLOT_HEIGHT + connectorLineOffset));
  const rightConnectorYs = new Set(rightNodes.map((_, index) => index * SLOT_HEIGHT + connectorLineOffset));
  const lines: string[] = [];

  for (let slot = 0; slot < slotCount; slot++) {
    const leftBox = renderArchitectureBox(leftNodes[slot], colors);
    const centerBox = renderArchitectureBox(slot === centerSlot ? centerNode : undefined, colors);
    const rightBox = renderArchitectureBox(rightNodes[slot], colors);

    for (let line = 0; line < BOX_HEIGHT; line++) {
      const y = slot * SLOT_HEIGHT + line;
      lines.push(
        `${leftBox[line]}${renderConnectorGap(y, leftConnectorYs, centerConnectorY, 'left', colors)}${centerBox[line]}${renderConnectorGap(y, rightConnectorYs, centerConnectorY, 'right', colors)}${rightBox[line]}`.trimEnd(),
      );
    }

    if (slot < slotCount - 1) {
      const y = slot * SLOT_HEIGHT + BOX_HEIGHT;
      lines.push(
        `${' '.repeat(BOX_WIDTH)}${renderConnectorGap(y, leftConnectorYs, centerConnectorY, 'left', colors)}${' '.repeat(BOX_WIDTH)}${renderConnectorGap(y, rightConnectorYs, centerConnectorY, 'right', colors)}`.trimEnd(),
      );
    }
  }

  const panelLines = renderDeploymentPanel(
    {
      projectName: input.projectName,
      environment: input.environment,
      workersEnabled: input.workersEnabled,
      workersConfig: input.workersConfig,
      showWorkersConfig: input.showWorkersConfig !== false,
      renderedAt: input.renderedAt ?? new Date(),
    },
    colors,
  );
  const rowCount = Math.max(lines.length, panelLines.length);
  const panelWidth = Math.max(...panelLines.map(visibleArchitectureWidth));

  return Array.from({ length: rowCount }, (_, index) => {
    const panelLine = panelLines[index] ?? '';
    const paddedPanelLine = `${panelLine}${' '.repeat(Math.max(0, panelWidth - visibleArchitectureWidth(panelLine)))}`;
    return `${paddedPanelLine}  ${colors.dim('│')}  ${lines[index] ?? ''}`.trimEnd();
  }).join('\n');
}

function getPackageName(projectDir: string): string | null {
  try {
    const raw = execSync('node -p "require(\'./package.json\').name"', {
      cwd: projectDir,
      encoding: 'utf-8',
      stdio: ['pipe', 'pipe', 'pipe'],
    }).trim();
    return raw.startsWith('@') ? (raw.split('/')[1] ?? raw) : raw;
  } catch {
    return null;
  }
}

function getGitBranch(projectDir: string): string | null {
  try {
    return execSync('git rev-parse --abbrev-ref HEAD', {
      cwd: projectDir,
      encoding: 'utf-8',
      stdio: ['pipe', 'pipe', 'pipe'],
    }).trim();
  } catch {
    return null;
  }
}

export async function zipOutput(
  projectDir: string,
  options: { includeWorkersManifest?: boolean } = {},
): Promise<string> {
  const outputDir = join(projectDir, '.mastra', 'output');
  const tmpDir = join(tmpdir(), 'mastra-deploy');
  await mkdir(tmpDir, { recursive: true });
  const zipPath = join(tmpDir, `deploy-${Date.now()}.zip`);

  return new Promise((resolvePromise, reject) => {
    const output = createWriteStream(zipPath);
    const archive = new ZipArchive({ zlib: { level: 6 } });

    output.on('close', () => resolvePromise(zipPath));
    archive.on('error', reject);

    archive.pipe(output);
    // `**` skips dotfiles by default; `dot` keeps the .npmrc that the build
    // copies into the output so private-registry installs work remotely.
    archive.glob(
      '**',
      {
        cwd: outputDir,
        ignore: [
          'node_modules/**',
          // Exclude build-only worker introspection artifacts left by older CLI builds.
          'worker-manifest.mjs',
          'worker-manifest.mjs.map',
          'workers-config.mjs',
          'workers-config.mjs.map',
          ...(options.includeWorkersManifest === false ? ['workers.json'] : []),
        ],
        dot: true,
      },
      { prefix: 'output' },
    );
    void archive.finalize();
  });
}

/* ------------------------------------------------------------------ */
/*  Resolve org                                                       */
/* ------------------------------------------------------------------ */

async function resolveOrg(
  token: string,
  projectConfig: { organizationId?: string } | null,
  flagOrg?: string,
): Promise<{ orgId: string; orgName: string }> {
  const envOrgId = process.env.MASTRA_ORG_ID;
  if (envOrgId) {
    return { orgId: envOrgId, orgName: envOrgId };
  }

  if (flagOrg) {
    const orgs = await fetchOrgs(token);
    const match = orgs.find(o => o.id === flagOrg);
    return { orgId: flagOrg, orgName: match?.name ?? flagOrg };
  }

  if (projectConfig?.organizationId) {
    const orgs = await fetchOrgs(token);
    const match = orgs.find(o => o.id === projectConfig.organizationId);
    if (match) {
      return { orgId: match.id, orgName: match.name };
    }
  }

  const currentOrgId = await getCurrentOrgId();
  const orgs = await fetchOrgs(token);

  if (currentOrgId) {
    const match = orgs.find(o => o.id === currentOrgId);
    if (match) {
      return { orgId: match.id, orgName: match.name };
    }
  }

  if (orgs.length === 1) {
    return { orgId: orgs[0]!.id, orgName: orgs[0]!.name };
  }

  if (orgs.length === 0) {
    throw new Error(`You have no organizations. Please create one at ${MASTRA_STUDIO_URL}`);
  }

  const selected = await p.select({
    message: 'Select an organization',
    options: orgs.map(o => ({ value: o.id, label: `${o.name} (${o.id})` })),
  });

  if (p.isCancel(selected)) {
    p.cancel('Deploy cancelled.');
    process.exit(0);
  }

  const selectedOrg = orgs.find(o => o.id === selected)!;
  return { orgId: selectedOrg.id, orgName: selectedOrg.name };
}

/* ------------------------------------------------------------------ */
/*  Resolve project                                                   */
/* ------------------------------------------------------------------ */

type ProjectResolution =
  | { existing: true; projectId: string; projectName: string; projectSlug: string }
  | { existing: false; projectName: string };

export async function resolveProject(
  token: string,
  orgId: string,
  projectConfig: { projectId?: string; projectName?: string; projectSlug?: string; organizationId?: string } | null,
  flagProject?: string,
  defaultName?: string | null,
  autoAccept?: boolean,
): Promise<ProjectResolution> {
  const envProjectId = process.env.MASTRA_PROJECT_ID;
  if (envProjectId) {
    const projects = await fetchProjects(token, orgId).catch(() => []);
    const project = projects.find(candidate => candidate.id === envProjectId);
    return {
      existing: true,
      projectId: envProjectId,
      projectName: project?.name ?? envProjectId,
      projectSlug: project?.slug ?? project?.name ?? envProjectId,
    };
  }

  if (flagProject) {
    const projects = await fetchProjects(token, orgId);
    const byId = projects.find(proj => proj.id === flagProject);
    const bySlug = projects.find(proj => proj.slug === flagProject);
    const byName = projects.filter(proj => proj.name === flagProject);
    if (!byId && !bySlug && byName.length > 1) {
      p.cancel(
        `Multiple projects are named "${flagProject}". Pass --project with the project id or slug to disambiguate.`,
      );
      process.exit(1);
    }
    const match = byId ?? bySlug ?? (byName.length === 1 ? byName[0] : undefined);
    if (match) {
      return { existing: true, projectId: match.id, projectName: match.name, projectSlug: match.slug ?? match.name };
    }
    return { existing: false, projectName: flagProject };
  }

  if (projectConfig?.projectId && projectConfig.organizationId === orgId) {
    return {
      existing: true,
      projectId: projectConfig.projectId,
      projectName: projectConfig.projectName ?? projectConfig.projectId,
      projectSlug: projectConfig.projectSlug ?? projectConfig.projectName ?? projectConfig.projectId,
    };
  }

  const projects = await fetchProjects(token, orgId);
  const nameMatches = defaultName
    ? projects.filter(proj => proj.name === defaultName || proj.slug === defaultName)
    : [];

  if (projects.length > 0) {
    if (autoAccept) {
      if (nameMatches.length === 1) {
        const m = nameMatches[0]!;
        return { existing: true, projectId: m.id, projectName: m.name, projectSlug: m.slug ?? m.name };
      }
      throw new Error(
        `Found ${projects.length} existing project(s) in this organization. Pass --project <id-or-slug> to select one, or re-run without --yes to choose interactively.`,
      );
    }

    const CREATE_NEW = '__create_new__';
    const initialValue = nameMatches.length === 1 ? nameMatches[0]!.id : projects[0]!.id;
    const selected = await p.select({
      message: 'Select a project to deploy to',
      initialValue,
      options: [
        ...projects.map(proj => ({
          value: proj.id,
          label: `${proj.name} (${proj.id})`,
        })),
        { value: CREATE_NEW, label: defaultName ? `＋ Create new project "${defaultName}"` : '＋ Create new project' },
      ],
    });

    if (p.isCancel(selected)) {
      p.cancel('Deploy cancelled.');
      process.exit(0);
    }

    if (selected !== CREATE_NEW) {
      const match = projects.find(proj => proj.id === selected)!;
      return { existing: true, projectId: match.id, projectName: match.name, projectSlug: match.slug ?? match.name };
    }
  }

  const name = defaultName;
  if (!name) {
    throw new Error('Could not determine project name from package.json. Use --project to specify one.');
  }

  return { existing: false, projectName: name };
}

/* ------------------------------------------------------------------ */
/*  Project type + project creation                                   */
/* ------------------------------------------------------------------ */

function toServerProjectRegion(region: string | undefined): ServerProjectRegion | undefined {
  return region === 'eu' || region === 'us' ? region : undefined;
}

/**
 * Create the platform project for a first deploy.
 *
 * Factory projects go through the server project endpoint with
 * `factoryEnabled: true`, the same call `create-factory` makes. The platform
 * only provisions factory backing (workspace sandboxes, `<slug>.factory.*`
 * route) for projects created with that flag, and the unified deploy path
 * has no way to set it afterwards. Everything else keeps using the studio
 * project endpoint.
 */
export async function createDeployProject(
  token: string,
  orgId: string,
  projectName: string,
  opts: { projectType?: string; region?: string } = {},
): Promise<{ id: string; name: string; slug: string | null }> {
  if (opts.projectType === 'factory') {
    const region = toServerProjectRegion(opts.region);
    return createServerProject(token, orgId, projectName, {
      factoryEnabled: true,
      ...(region ? { region } : {}),
    });
  }
  return createProject(token, orgId, projectName);
}

/**
 * Whether an existing project was created as a Factory project. Reads the
 * studio project list, which is the only CLI-facing endpoint that returns
 * the flag. `undefined` means it could not be determined (lookup failed or
 * the project was not in the list), in which case the caller proceeds.
 */
export async function lookupProjectFactoryFlag(
  token: string,
  orgId: string,
  projectId: string,
): Promise<boolean | undefined> {
  try {
    const projects = await fetchStudioProjects(token, orgId);
    const match = projects.find(project => project.id === projectId);
    return typeof match?.factoryEnabled === 'boolean' ? match.factoryEnabled : undefined;
  } catch {
    return undefined;
  }
}

export type NonFactoryTargetChoice = 'create' | 'deploy';

/**
 * A Factory build deployed into a project created without the factory flag
 * runs as a plain server deployment: no workspace sandboxes, and the
 * `<slug>.factory.*` route is never registered. The flag cannot be added
 * afterwards on the unified path, so the useful way out is a new project.
 * Offer that before touching the environment. Under `--yes` there is nobody
 * to ask, so warn and keep the requested target.
 */
export async function resolveNonFactoryTarget(input: {
  projectName: string;
  /** Name for a replacement project; `null` when package.json has no name. */
  newProjectName: string | null;
  autoAccept: boolean;
}): Promise<NonFactoryTargetChoice> {
  const explanation = [
    `This directory was scaffolded as a Mastra Factory, but the platform project "${input.projectName}" was created without Factory support, and that can't be added later.`,
    `Deploying there runs it as a plain server: no workspace sandboxes, and the Factory URL will not work.`,
  ];

  if (input.autoAccept) {
    p.log.warn(explanation.join('\n'));
    return 'deploy';
  }

  // The explanation rides on the prompt message: clack wraps prompt text to
  // the terminal width and keeps the guide bar on every line, which `log.*`
  // does not do.
  const choice = await p.select({
    message: [...explanation, 'How do you want to continue?'].join('\n'),
    options: [
      ...(input.newProjectName
        ? [
            {
              value: 'create' as const,
              label: `Create a new Factory project "${input.newProjectName}" and deploy there`,
              hint: 'recommended',
            },
          ]
        : []),
      { value: 'deploy' as const, label: `Deploy to "${input.projectName}" anyway as a plain server` },
      { value: 'cancel' as const, label: 'Cancel' },
    ],
  });

  if (p.isCancel(choice) || choice === 'cancel') {
    p.cancel('Deploy cancelled.');
    process.exit(0);
  }

  return choice;
}

async function promptDeployRegion(): Promise<string> {
  const selectedRegion = await p.select({
    message: 'Select a deployment region',
    initialValue: 'us',
    options: [
      { value: 'us', label: 'United States' },
      { value: 'eu', label: 'Europe' },
    ],
  });

  if (p.isCancel(selectedRegion)) {
    p.cancel('Deploy cancelled.');
    process.exit(0);
  }

  return selectedRegion;
}

/* ------------------------------------------------------------------ */
/*  Resolve environment                                               */
/* ------------------------------------------------------------------ */

type EnvironmentResolution =
  | { existing: true; environment: Environment }
  | { existing: false; name: string; type: 'production' | 'staging' | 'preview'; region?: string };

export async function resolveEnvironment(
  token: string,
  orgId: string,
  projectId: string,
  envName: string,
  autoAccept: boolean,
  requestedRegion?: string,
): Promise<EnvironmentResolution> {
  const environments = await fetchEnvironments(token, orgId, projectId);

  // Try to find by name (case-insensitive)
  const existing = environments.find(env => env.name.toLowerCase() === envName.toLowerCase());

  if (existing) {
    return { existing: true, environment: existing };
  }

  // Environment doesn't exist - determine type and prepare to create
  const envType =
    envName.toLowerCase() === 'production' ? 'production' : envName.toLowerCase() === 'staging' ? 'staging' : 'preview';

  // Skip the "create it?" prompt for production. A first deploy naturally
  // creates production — the confirmation is noise. Still prompt for
  // non-standard names in case the user made a typo (e.g. `--env prodcution`).
  if (!autoAccept && envType !== 'production') {
    const confirmed = await p.confirm({
      message: `Environment "${envName}" doesn't exist. Create it?`,
      initialValue: true,
    });

    if (p.isCancel(confirmed) || !confirmed) {
      p.cancel('Deploy cancelled.');
      process.exit(0);
    }
  }

  let region = requestedRegion;
  if (!region && !autoAccept) {
    region = await promptDeployRegion();
  }

  return { existing: false, name: envName, type: envType, ...(region ? { region } : {}) };
}

/* ------------------------------------------------------------------ */
/*  Upload to environment deploy endpoint                             */
/* ------------------------------------------------------------------ */

async function uploadToEnvironment(
  token: string,
  orgId: string,
  projectId: string,
  environmentId: string,
  zipBuffer: Buffer,
  opts: {
    gitBranch?: string;
    projectName: string;
    envVars?: Record<string, string>;
    mastraVersion?: string;
    disablePlatformObservability?: boolean;
  },
): Promise<{ id: string; uploadUrl: string }> {
  const apiUrl = process.env.MASTRA_PLATFORM_API_URL || 'https://platform.mastra.ai';

  // Create deploy via environment endpoint.
  //
  // The server reads gitBranch / mastraVersion / projectName from the
  // `x-*` headers (see servers/api/src/routes/environments.ts and
  // servers/api/src/routes/studio/deploys.ts) — passing them in the body
  // would silently no-op, which is what broke `mastraVersion` flowing
  // through to the route entry and the studio asset build.
  const createHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'x-organization-id': orgId,
    'x-project-name': opts.projectName,
  };
  if (opts.gitBranch) createHeaders['x-git-branch'] = opts.gitBranch;
  if (opts.mastraVersion) createHeaders['x-mastra-version'] = opts.mastraVersion;

  const createBody: Record<string, unknown> = {};
  if (opts.envVars) createBody.envVars = opts.envVars;
  if (opts.disablePlatformObservability !== undefined) {
    createBody.disablePlatformObservability = opts.disablePlatformObservability;
  }

  const createResp = await fetch(`${apiUrl}/v1/projects/${projectId}/environments/${environmentId}/deploy`, {
    method: 'POST',
    headers: createHeaders,
    body: JSON.stringify(createBody),
  });

  if (!createResp.ok) {
    const err = await createResp.json().catch(() => ({}));
    throw new Error(`Failed to create deploy: ${(err as { detail?: string }).detail || createResp.statusText}`);
  }

  const { deploy } = (await createResp.json()) as { deploy: { id: string; uploadUrl: string } };

  // Upload artifact
  const uploadResp = await fetch(deploy.uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/zip',
    },
    body: zipBuffer,
  });

  if (!uploadResp.ok) {
    throw new Error(`Failed to upload artifact: ${uploadResp.statusText}`);
  }

  // Signal upload complete — uses net-new env-scoped endpoint so the
  // unified-runtime CLI never touches /v1/studio/*.
  const completeResp = await fetch(
    `${apiUrl}/v1/projects/${projectId}/environments/${environmentId}/deploys/${deploy.id}/upload-complete`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'x-organization-id': orgId,
      },
    },
  );

  if (!completeResp.ok) {
    const err = await completeResp.json().catch(() => ({}));
    throw new Error(`Failed to complete upload: ${(err as { detail?: string }).detail || completeResp.statusText}`);
  }

  return deploy;
}

interface UnifiedDeployStatus {
  id: string;
  status: string;
  instanceUrl: string | null;
  error: string | null;
}

interface PollDeployOptions {
  /** Print every log line instead of the rolling tail shown on a TTY. */
  showAllLogs?: boolean;
  /** Receives every raw log entry, so a failure excerpt can be printed later. */
  collectLogs?: LogCollector;
}

/**
 * Poll the net-new env-scoped status endpoint until the deploy reaches a
 * terminal state. Kept inside the deploy command so the unified runtime
 * never reaches into ../studio/ for transport.
 */
/** Set once the log stream has connected; a connected stream is drained before it is stopped. */
interface StreamState {
  connected: boolean;
}

/** How long a connected stream may keep delivering after the deploy reached a terminal state. */
const SSE_DRAIN_MS = 500;

async function streamEnvironmentDeployLogs(
  token: string,
  orgId: string,
  projectId: string,
  environmentId: string,
  deployId: string,
  signal: AbortSignal,
  logWriter: DeployLogWriter,
  state: StreamState,
): Promise<void> {
  // Small delay to let the deploy pipeline start before requesting logs
  await abortableDelay(2000, signal);
  if (signal.aborted) return;

  const apiUrl = process.env.MASTRA_PLATFORM_API_URL || 'https://platform.mastra.ai';
  const url = `${apiUrl}/v1/projects/${projectId}/environments/${environmentId}/deploys/${deployId}/logs/stream`;

  const resp = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      'x-organization-id': orgId,
      Accept: 'text/event-stream',
    },
    signal,
  });

  if (!resp.ok || !resp.body) return;
  state.connected = true;

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let skipNextUrlMeta = false;

  while (!signal.aborted) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (!line.startsWith('data:')) continue;
      const data = line.slice(5).trim();
      if (!data) continue;
      // Filter internal server startup logs — public URL is shown by CLI after deploy
      if (data.includes('Mastra API running') || data.includes('Studio available')) {
        skipNextUrlMeta = true;
        continue;
      }
      if (skipNextUrlMeta) {
        skipNextUrlMeta = false;
        if (/^(\x1b\[\d+m)*url(\x1b\[\d+m)*:/.test(data)) continue;
      }
      logWriter.write(data);
    }
  }
}

async function pollEnvironmentDeploy(
  token: string,
  orgId: string,
  projectId: string,
  environmentId: string,
  deployId: string,
  maxWaitMs = 600_000,
  options: PollDeployOptions = {},
): Promise<UnifiedDeployStatus> {
  const apiUrl = process.env.MASTRA_PLATFORM_API_URL || 'https://platform.mastra.ai';
  const url = `${apiUrl}/v1/projects/${projectId}/environments/${environmentId}/deploys/${deployId}`;
  const start = Date.now();
  let currentToken = token;

  // Stream logs in parallel with status polling
  const logAbort = new AbortController();
  const logWriter = createBarLogWriter({ showAll: options.showAllLogs, collect: options.collectLogs });
  const streamState: StreamState = { connected: false };
  const logsTask = streamEnvironmentDeployLogs(
    currentToken,
    orgId,
    projectId,
    environmentId,
    deployId,
    logAbort.signal,
    logWriter,
    streamState,
  ).catch(() => {});

  try {
    while (Date.now() - start < maxWaitMs) {
      const resp = await fetch(url, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
          'x-organization-id': orgId,
        },
      });

      if (resp.status === 401) {
        currentToken = await getToken();
        // Back off before retrying so a persistently-401 token cannot spin
        // the poll loop into a tight retry storm against the platform API.
        await new Promise(r => setTimeout(r, 2000));
        continue;
      }

      if (!resp.ok) {
        const err = (await resp.json().catch(() => ({}))) as { detail?: string };
        throw new Error(`Poll failed: ${err.detail || resp.statusText}`);
      }

      const { deploy } = (await resp.json()) as { deploy: UnifiedDeployStatus };

      if (deploy.status === 'running' || deploy.status === 'failed' || deploy.status === 'stopped') {
        return deploy;
      }

      await new Promise(r => setTimeout(r, 2000));
    }

    throw new Error('Deploy timed out');
  } finally {
    // Give a connected stream a moment to deliver events already in flight,
    // stop it, wait for the reader to settle, then draw whatever is queued so
    // nothing is lost and nothing prints after the outcome message.
    if (streamState.connected) await Promise.race([logsTask, abortableDelay(SSE_DRAIN_MS)]);
    logAbort.abort();
    await logsTask;
    logWriter.flush();
  }
}

/* ------------------------------------------------------------------ */
/*  Main unified deploy action                                        */
/* ------------------------------------------------------------------ */

export interface DeployOptions {
  env?: string;
  org?: string;
  project?: string;
  yes?: boolean;
  config?: string;
  skipBuild?: boolean;
  skipPreflight?: boolean;
  region?: string;
  debug?: boolean;
  envFile?: string;
  /**
   * How to run background workers for this deploy:
   *   `dedicated`  — provision a dedicated workers service (errors if the
   *                  deploy env lacks the Redis requirement).
   *   `in-process` — run background tasks inside the API server container;
   *                  spins down an existing workers service.
   * When omitted, the CLI prompts on the first deploy where workers are
   * detected but the environment has no workers service yet.
   */
  workers?: WorkersDeployMode;
}

export async function unifiedDeployAction(dir: string | undefined, opts: DeployOptions) {
  if (opts.workers !== undefined && opts.workers !== 'dedicated' && opts.workers !== 'in-process') {
    throw new Error(`--workers must be "dedicated" or "in-process" (got "${String(opts.workers)}")`);
  }
  if (opts.region !== undefined && opts.region !== 'us' && opts.region !== 'eu') {
    throw new Error(`--region must be "us" or "eu" (got "${String(opts.region)}")`);
  }
  const analytics = getAnalytics();
  if (!analytics) {
    return runUnifiedDeploy(dir, opts);
  }
  return analytics.trackCommandExecution({
    command: 'mastra deploy',
    args: {
      env: opts.env || 'production',
      yes: Boolean(opts.yes),
      skipBuild: Boolean(opts.skipBuild),
      skipPreflight: Boolean(opts.skipPreflight),
      hasOrg: Boolean(opts.org),
      hasProject: Boolean(opts.project),
      hasEnvFile: Boolean(opts.envFile),
      hasConfig: Boolean(opts.config),
      debug: Boolean(opts.debug),
      workers: opts.workers ?? 'prompt',
      headless: Boolean(process.env.MASTRA_API_TOKEN),
      targetApi: bucketApiHost(MASTRA_PLATFORM_API_URL),
    },
    execution: () => runUnifiedDeploy(dir, opts),
    origin: process.env.MASTRA_ANALYTICS_ORIGIN as CLI_ORIGIN | undefined,
  });
}

async function runUnifiedDeploy(dir: string | undefined, opts: DeployOptions) {
  const targetDir = resolve(dir || process.cwd());
  await assertDeployDir(dir, targetDir);
  loadDeployEnvFromDotenv(targetDir);

  const isHeadless = Boolean(process.env.MASTRA_API_TOKEN);
  if (isHeadless && (!process.env.MASTRA_ORG_ID || !process.env.MASTRA_PROJECT_ID)) {
    throw new Error('MASTRA_ORG_ID and MASTRA_PROJECT_ID are required when MASTRA_API_TOKEN is set');
  }

  const autoAccept = opts.yes ?? isHeadless;
  const skipPreflight = opts.skipPreflight || process.env.MASTRA_SKIP_PREFLIGHT === '1';
  const envName = opts.env || 'production';

  p.intro(`${pc.bold('mastra deploy')} → ${pc.cyan(envName)}`);

  // Gather context
  const packageName = getPackageName(targetDir);
  const gitBranch = getGitBranch(targetDir);
  const mastraVersion = getMastraVersion(targetDir);
  // Detected up front: a new Factory project must be created with the
  // factory flag, and the build step needs it for Factory UI staleness.
  const projectType = await detectProjectType(targetDir);
  const isFactoryProject = projectType === 'factory';

  // Step 1: Auth
  const token = await getToken();
  const userId = isHeadless ? undefined : (await loadCredentials())?.user.id;

  // Step 2: Load existing project config
  const projectConfig = await loadProjectConfig(targetDir, opts.config);

  // Step 3: Resolve org
  const { orgId, orgName } = await resolveOrg(token, projectConfig, opts.org);

  // Step 4: Resolve project (does NOT create yet)
  let resolution = await resolveProject(token, orgId, projectConfig, opts.project, packageName, autoAccept);

  if (resolution.existing && isFactoryProject) {
    const targetIsFactory = await lookupProjectFactoryFlag(token, orgId, resolution.projectId);
    if (targetIsFactory === false) {
      const choice = await resolveNonFactoryTarget({
        projectName: resolution.projectName,
        newProjectName: packageName,
        autoAccept,
      });
      if (choice === 'create' && packageName) {
        resolution = { existing: false, projectName: packageName };
      }
    }
  }

  let projectId: string;
  let projectName: string;
  let projectSlug: string;
  // Region for a newly created environment. A new Factory project asks for
  // it once and reuses the answer for both the project and the environment.
  let requestedRegion = opts.region;

  if (resolution.existing) {
    projectId = resolution.projectId;
    projectName = resolution.projectName;
    projectSlug = resolution.projectSlug;
  } else {
    projectName = resolution.projectName;

    p.note(
      [
        `Organization:  ${orgName}`,
        `Project:       ${projectName} (new${isFactoryProject ? ' Factory project' : ''})`,
        `Environment:   ${envName}`,
        `Directory:     ${targetDir}`,
        ...(gitBranch ? [`Git branch:    ${gitBranch}`] : []),
        ...(mastraVersion ? [`Mastra:        ${mastraVersion}`] : []),
      ].join('\n'),
      'Deploy settings',
    );

    if (!autoAccept) {
      const confirmed = await p.confirm({
        message: 'Create project and deploy?',
      });

      if (p.isCancel(confirmed) || !confirmed) {
        p.cancel('Deploy cancelled.');
        process.exit(0);
      }
    }

    if (isFactoryProject && !requestedRegion && !autoAccept) {
      requestedRegion = await promptDeployRegion();
    }

    // Create the project
    const project = await createDeployProject(token, orgId, projectName, {
      projectType,
      region: requestedRegion,
    });
    projectId = project.id;
    projectSlug = project.slug ?? project.name;
    p.log.success(`Created ${isFactoryProject ? 'Factory ' : ''}project "${projectName}"`);

    // Save the project link
    await saveProjectConfig(
      targetDir,
      getProjectConfigToSave(projectId, projectName, projectSlug, orgId, projectConfig),
      opts.config,
    );
    p.log.success(`Saved ${opts.config || '.mastra-project.json'}`);
  }

  // Step 5: Resolve environment (auto-create production if first deploy)
  const envResolution = await resolveEnvironment(token, orgId, projectId, envName, autoAccept, requestedRegion);

  let environment: Environment;

  if (envResolution.existing) {
    environment = envResolution.environment;
  } else {
    // Create the environment
    environment = await createEnvironment(token, orgId, projectId, {
      name: envResolution.name,
      type: envResolution.type,
      ...(envResolution.region ? { region: envResolution.region } : {}),
    });
    p.log.success(`Created ${envResolution.type} environment "${envResolution.name}"`);
  }

  // Show confirmation for existing project
  if (resolution.existing) {
    const isAlreadyLinked =
      projectConfig?.projectId === projectId &&
      projectConfig.organizationId === orgId &&
      projectConfig.projectName === projectName &&
      projectConfig.projectSlug === projectSlug;

    p.note(
      [
        `Organization:  ${orgName}`,
        `Project:       ${projectName}`,
        `Environment:   ${environment.name} (${environment.slug})`,
        `Directory:     ${targetDir}`,
        ...(gitBranch ? [`Git branch:    ${gitBranch}`] : []),
        ...(mastraVersion ? [`Mastra:        ${mastraVersion}`] : []),
      ].join('\n'),
      'Deploy settings',
    );

    if (!autoAccept) {
      const confirmed = await p.confirm({
        message: 'Deploy with these settings?',
      });

      if (p.isCancel(confirmed) || !confirmed) {
        p.cancel('Deploy cancelled.');
        process.exit(0);
      }
    }

    if (!isAlreadyLinked) {
      await saveProjectConfig(
        targetDir,
        getProjectConfigToSave(projectId, projectName, projectSlug, orgId, projectConfig),
        opts.config,
      );
      p.log.success(`Saved ${opts.config || '.mastra-project.json'}`);
    }
  }

  // Step 6: Build + Zip + Upload + Poll
  const s = p.spinner();
  const tTotal = performance.now();

  let t: number;

  // Check build staleness
  const mastraDir = join(targetDir, 'src', 'mastra');
  const outputDirectory = join(targetDir, '.mastra');
  // Staleness hashing includes Factory UI inputs for factory projects.
  const staleness = await checkBuildStaleness(targetDir, mastraDir, outputDirectory, projectType);
  const workersManifestExists = await hasWorkersManifest(targetDir);
  const workerManifestChecked = await hasWorkerManifestCheck(targetDir);
  const buildNeedsRefresh = deployBuildNeedsRefresh(staleness, workersManifestExists, workerManifestChecked);

  if (opts.skipBuild) {
    if (staleness.isStale && staleness.reason !== 'no-build') {
      if (staleness.reason === 'hash-mismatch') {
        p.log.warn('Source files have changed since last build. Deploy may not reflect latest changes.');
      } else if (staleness.reason === 'no-manifest') {
        p.log.warn('No build manifest found. Cannot verify if build is up-to-date.');
      }
    }
    p.log.step('Skipping build (--skip-build)');
  } else if (buildNeedsRefresh) {
    t = performance.now();
    if (staleness.reason === 'hash-mismatch') {
      p.log.step('Source files changed, rebuilding...');
    } else if (staleness.reason === 'no-manifest') {
      p.log.step('Build manifest missing, rebuilding...');
    } else if (!workersManifestExists && !workerManifestChecked) {
      p.log.step('Build metadata is outdated, rebuilding...');
    }
    await runBuild(targetDir, { debug: opts.debug });
    await writeFile(workerManifestCheckPath(targetDir), WORKER_MANIFEST_CHECK_VERSION);
    p.log.step(`Build completed (${elapsed(performance.now() - t)})`);
  } else {
    p.log.step('Build is up-to-date, skipping rebuild');
  }

  // Verify build output exists
  const outputEntry = join(targetDir, '.mastra', 'output', 'index.mjs');
  try {
    await access(outputEntry);
  } catch {
    throw new Error('.mastra/output/index.mjs not found — did the build succeed?');
  }

  // Auto-select .env.<envName> when deploying to a named environment
  // (e.g. --env staging auto-selects .env.staging if it exists).
  //
  // envName comes from the --env CLI flag, so we validate it before
  // interpolating it into a file path. Only simple environment identifiers
  // (letters, digits, dot, dash, underscore) are allowed; anything with a
  // path separator or `..` traversal segment is ignored. This keeps a
  // hostile --env value from escaping the project directory and being read
  // (and re-uploaded) via readEnvVars.
  let envFile = opts.envFile;
  if (!envFile && /^[a-zA-Z0-9._-]+$/.test(envName) && !envName.includes('..')) {
    const envNameFile = `.env.${envName}`;
    const candidate = resolve(targetDir, envNameFile);
    const targetPrefix = resolve(targetDir) + '/';
    if (candidate.startsWith(targetPrefix)) {
      try {
        await access(candidate);
        envFile = envNameFile;
      } catch {
        // No matching env file for this environment name — fall through to default logic
      }
    }
  }

  // If the user didn't pass --env-file and no ambient .env* file exists,
  // skip the local env-var upload entirely and let the platform use the
  // env vars stored on the target environment. The server-side deploy
  // handler merges request envVars over environment.envVars, so an empty
  // (absent) envVars payload cleanly falls back to what's already stored.
  let envVars: Record<string, string> = {};
  const hasAmbientEnvFile = envFile ? true : (await getDeployEnvFiles(targetDir)).length > 0;
  if (hasAmbientEnvFile) {
    envVars = await readEnvVars(targetDir, { autoAccept, envFile });
  }
  const envCount = Object.keys(envVars).length;
  if (envCount > 0) {
    p.log.step(`Found ${envCount} env var(s)`);
  } else if (hasAmbientEnvFile) {
    p.log.step('No env vars found in selected env file');
  } else {
    p.log.step('No local env file — using env vars stored on the environment');
  }

  // Warn before overwriting env vars that already exist on the environment
  // with a different value. The platform merges request envVars over the
  // stored environment.envVars (request wins), so these keys get replaced.
  // Only relevant when deploying to a pre-existing environment.
  if (envCount > 0 && envResolution.existing) {
    const overwrittenKeys = getOverwrittenEnvKeys(environment.envVars, envVars);
    if (overwrittenKeys.length > 0) {
      p.log.warn(
        `This deploy will overwrite ${overwrittenKeys.length} existing env var(s) on "${environment.name}":\n` +
          overwrittenKeys.map(key => `  • ${key}`).join('\n'),
      );

      if (!autoAccept) {
        const confirmed = await p.confirm({
          message: 'Overwrite these env vars?',
          initialValue: true,
        });

        if (p.isCancel(confirmed) || !confirmed) {
          p.cancel('Deploy cancelled.');
          process.exit(0);
        }
      }
    }
  }

  const deploymentEnv = mergePreflightEnvVars(environment.envVars, envVars);

  // Managed-resource env var names (e.g. an attached Redis injects
  // REDIS_URL at deploy time). Auto-provisioning during preflight can grow
  // this set; the workers-mode gate below needs the final picture.
  let managedEnvVarNames = environment.managedEnvVarNames ?? null;

  // Pre-upload validation. Preflight sees the same env picture the platform
  // applies at deploy time: request env vars merged over the environment's
  // stored vars (request wins), so platform-stored vars don't false-alarm.
  if (!skipPreflight) {
    let issues = await preflightBuildOutput(targetDir, deploymentEnv, {
      hasEnvFile: hasAmbientEnvFile,
      // Managed resources (e.g. attached databases) inject vars at deploy
      // time; the platform exposes their names on the environment. Absent
      // field = older platform = incomplete env picture (soften to warnings).
      managedEnvVarNames: environment.managedEnvVarNames ?? null,
      // Use the environment NAME (e.g. `production`, `staging`), not the
      // slug: some platforms derive the production env's slug from the
      // project name (`my-app-xyz-1234`), which the env-resolver accepts
      // but is jarring in a printed remediation command. The name is what
      // the user actually types.
      environmentName: environment.name,
      // Unified deploy is the only flow that provisions a worker service
      // from the build manifest, so it alone opts into the workers check.
      checkWorkers: true,
    });

    // If preflight flagged a blocking issue that a managed database would
    // fix (e.g. TURSO_DATABASE_URL missing), offer to attach one inline
    // rather than failing the deploy and asking the user to run
    // `mastra env db create` themselves.
    const autoProvisioned = await maybeAutoProvisionDatabases(issues, {
      token,
      orgId,
      projectId,
      projectName,
      projectSlug,
      environment: {
        id: environment.id,
        slug: environment.slug,
        name: environment.name,
        type: environment.type,
      },
      autoAccept,
    });
    if (autoProvisioned.provisioned.length > 0) {
      const attached = autoProvisioned.provisioned.map(d => `${d.name} (${d.kind})`).join(', ');
      p.log.success(`Attached managed database: ${attached}`);

      // Re-run preflight with the newly-attached vars folded into the
      // managed set. Without this, MISSING_ENV_VAR issues for the vars we
      // just provisioned (TURSO_AUTH_TOKEN, TURSO_DATABASE_URL) still show
      // as "not in the env file being deployed" — misleading right after
      // we told the user the DB was attached. Merging is enough; no need
      // to re-fetch the environment because attachDatabase's response is
      // authoritative for the vars it just injected.
      managedEnvVarNames = [...(environment.managedEnvVarNames ?? []), ...autoProvisioned.newlyManagedEnvVarNames];
      issues = await preflightBuildOutput(targetDir, deploymentEnv, {
        hasEnvFile: hasAmbientEnvFile,
        managedEnvVarNames,
        environmentName: environment.name,
        checkWorkers: true,
      });
    } else {
      issues = autoProvisioned.issues;
    }

    const outcome = await printPreflightIssues(issues, { autoAccept });
    if (outcome === 'blocked') {
      p.cancel('Deploy blocked by preflight errors.');
      process.exit(1);
    }
    if (outcome === 'cancelled') {
      p.cancel('Deploy cancelled.');
      process.exit(0);
    }
  }

  let workersConfig = await readWorkersConfig(targetDir);
  let workersEnabled = workerManifestHasEnabledWorkers(workersConfig);
  let includeWorkersManifest = true;
  let showWorkersConfig = true;

  if (workersEnabled) {
    const gate = await applyPlatformWorkersFlagGate({ orgId, userId, analytics: getAnalytics() });
    if (gate === 'suppressed') {
      includeWorkersManifest = false;
      showWorkersConfig = false;
      workersEnabled = false;
    }
  }

  const environmentHasWorkerService = Boolean(environment.workerProviderServiceId);
  const redisRequirementMet = hasWorkersRedisRequirement(deploymentEnv, managedEnvVarNames);
  let workersMode: WorkersDeployMode;
  try {
    workersMode = await resolveWorkersDeployMode({
      workersEnabled,
      redisRequirementMet,
      environmentHasWorkerService,
      workersOption: opts.workers,
      autoAccept,
      promptConfirm: message => p.confirm({ message, initialValue: true }),
      isCancel: (value): value is symbol => p.isCancel(value),
    });
  } catch (error) {
    if (error instanceof WorkersRedisRequirementError) {
      p.cancel(error.message);
      process.exit(1);
    }
    throw error;
  }
  if (workersMode === 'in-process' && workersEnabled) {
    includeWorkersManifest = false;
    if (!redisRequirementMet) {
      p.log.warn(
        'Background workers are configured, but the deploy env has no usable REDIS_URL — the platform needs Redis (pub/sub) to coordinate a dedicated workers service. Background tasks will run in-process inside the API server container.',
      );
    } else {
      p.log.step('Running background tasks in-process inside the API server container (no dedicated workers service)');
    }
    // Reflect in-process mode in the overview without deleting reusable build metadata.
    workersConfig = null;
    workersEnabled = false;
  }
  if (workersMode === 'in-process' && environmentHasWorkerService) {
    p.log.warn(
      'This environment has a dedicated workers service — deploying without a workers manifest will spin it down. Background tasks will run in-process inside the API server container.',
    );
  }
  if (opts.workers === 'dedicated' && !workersEnabled) {
    p.log.warn(
      'Ignoring --workers dedicated: the build emitted no enabled workers manifest — the Mastra config disables workers (`workers: false`) or the account is not enrolled. No dedicated workers service will be provisioned.',
    );
  }

  const publicUrls = derivePublicUrls(environment.slug, projectType);
  let databases: ProjectDatabase[] = [];
  try {
    databases = await fetchDatabases(token, orgId, projectId);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    p.log.warn(`Could not load attached databases for the deployment architecture (${message}).`);
  }
  p.note(
    renderDeploymentArchitecture({
      projectName,
      environment,
      serverLabel: publicUrls.serverLabel,
      workersEnabled,
      workersConfig,
      showWorkersConfig,
      databases,
      observabilityEnabled: projectConfig?.disablePlatformObservability !== true,
    }),
    'Deployment Overview',
  );

  t = performance.now();
  s.start('Zipping build artifact...');
  const zipPath = await zipOutput(targetDir, { includeWorkersManifest });
  const zipStat = await stat(zipPath);
  const sizeKB = zipStat.size / 1024;
  const sizeLabel = sizeKB > 1024 ? `${(sizeKB / 1024).toFixed(1)}MB` : `${sizeKB.toFixed(1)}KB`;
  s.stop(`Created ${sizeLabel} archive (${elapsed(performance.now() - t)})`);

  t = performance.now();
  s.start('Uploading...');
  const zipBuffer = await readFile(zipPath);
  const deployResult = await uploadToEnvironment(token, orgId, projectId, environment.id, zipBuffer, {
    gitBranch: gitBranch ?? undefined,
    projectName,
    envVars: envCount > 0 ? envVars : undefined,
    mastraVersion: mastraVersion ?? undefined,
    disablePlatformObservability: projectConfig?.disablePlatformObservability === true,
  });
  s.stop(`Uploaded (${elapsed(performance.now() - t)})`);

  await rm(zipPath, { force: true });

  p.log.step('Waiting for deploy to finish...');
  // With --debug every line is already on screen, so no excerpt is needed.
  const collectedLogs = opts.debug ? undefined : createLogCollector();
  const finalStatus = await pollEnvironmentDeploy(token, orgId, projectId, environment.id, deployResult.id, undefined, {
    showAllLogs: opts.debug,
    collectLogs: collectedLogs,
  });

  if (finalStatus.status === 'running') {
    p.log.info(`  Studio: ${pc.cyan(publicUrls.studioUrl)}`);
    p.log.info(`  ${publicUrls.serverLabel}: ${pc.cyan(publicUrls.serverUrl)}`);
    p.outro(`Deploy succeeded in ${elapsed(performance.now() - tTotal)}!`);
  } else {
    printDeployFailure({
      message:
        finalStatus.status === 'failed'
          ? `Deploy failed: ${finalStatus.error}`
          : `Deploy ended with status: ${finalStatus.status}`,
      collectedLogs: collectedLogs?.entries() ?? [],
      dashboardUrl: deployDashboardUrl('environment', { orgId, projectId, deployId: deployResult.id }),
      showAllLogs: opts.debug,
    });
    // Progressive discovery: only hint at `mastra env diagnosis` when the
    // command is actually registered (same feature gate as index.ts). The
    // failed-deploy webhook has already inserted a PENDING diagnosis row,
    // so the command returns "in progress" immediately rather than 404-ing
    // while the agent runs.
    if (finalStatus.status === 'failed' && coreFeatures.has('deploy-diagnosis')) {
      p.log.info(`Run \`mastra env diagnosis ${deployResult.id}\` for suggestions.`);
    }
    process.exit(1);
  }
}
